import { useEffect, useState } from 'react';
import Card from '@components/shared/Card/Card';
import { useTexts } from 'utils/useTexts';
import getOfferTypeIcon from 'utils/getOfferTypeIcon';

import { AssetLinks } from '@assets/assets';
import useEffectOnce from '@shared/hooks/useEffectOnce';
import { useAnalytics } from '@shared/hooks/useAnalytics';
import { SEGMENT_SCREEN_PARAMS } from '@shared/constants/segmentConstants';
import { useAppContext } from '@context/appContext';

const OffRampPage = () => {
  const { t } = useTexts();
  const { trackClick, trackPage } = useAnalytics(SEGMENT_SCREEN_PARAMS.THANK_YOU_PAGE, false);
  const { screenOrder } = useAppContext();
  const [checklistItems, setCheckListItems] = useState([]);
  const [isSoftPullLoanPresent, setIsSoftPullLoanPresent] = useState(false);

  const storedDetails = JSON.parse(localStorage.getItem('iLendingCDX'));

  useEffect(() => {
    if (screenOrder.length) {
      const allChecklistItems = [
        {
          pageGroupName: 'CREATE_CONTACT',
          title: t('thankYou.checklistItems.personalInformation'),
          completed: true,
          disabled: false,
        },
        {
          pageGroupName: 'PLATE_TO_VIN',
          title: t('thankYou.checklistItems.vehicleInformation'),
          completed: true,
          disabled: false,
        },
        {
          pageGroupName: 'EMPLOYMENT',
          title: t('thankYou.checklistItems.employmentAndIncome'),
          completed: storedDetails?.employmentAndIncomeAdded ?? false,
          disabled: false,
        },
        {
          pageGroupName: 'NEXT_STEPS',
          title: t('thankYou.checklistItems.documents'),
          completed: storedDetails?.documentsUploaded ?? false,
          disabled: false,
        },
      ];

      const filteredChecklistItems = allChecklistItems.filter((item) => {
        return screenOrder.some((page) => page.pageName === item.pageGroupName);
      });
      const hasSoftPullLoan = screenOrder.some((page) => page.pageName === 'SOFT_PULL_LOAN');
      setIsSoftPullLoanPresent(hasSoftPullLoan);
      setCheckListItems(filteredChecklistItems);
    }
  }, [screenOrder]);

  useEffect(() => {
    return () => {
      localStorage.clear();
    };
  }, []);

  useEffectOnce(() => trackPage(t('pageTitles.OffRamp')));

  const selectedOfferData = {
    id: storedDetails?.selectedOffer?.dealId,
    name: storedDetails?.selectedOffer?.offerType,
    price: storedDetails?.selectedOffer?.monthlyPaymentAmount,
    interest: storedDetails?.selectedOffer?.rate,
    term: storedDetails?.selectedOffer?.term,
  };

  const handlePhoneNumberClick = () => {
    trackClick('CTA Clicked', {
      cta_text: 'Call' + t('contactPhoneNumber'),
      cta_type: 'phone',
      cta_context: 'off-ramp',
      interaction_type: 'cta_click',
      destination_url: 'tel:' + t('contactPhoneNumber'),
      value: 75,
      currency: 'USD',
    });
  };

  return (
    <div>
      <h1 className="iLendingCDX-form-title"> {t('thankYou.title')}</h1>
      <p className="iLendingCDX-form-title--small iLendingCDX-mb-4">{t('thankYou.yourEstimatedOffer')}</p>
      {isSoftPullLoanPresent && (
        <Card
          id={''}
          showSelectionStyle={true}
          icon={getOfferTypeIcon(selectedOfferData?.offerType ?? '')}
          name={selectedOfferData?.name}
          price={selectedOfferData?.price}
          interest={selectedOfferData?.interest}
          term={selectedOfferData?.term}
          selected={selectedOfferData.id}
          offer={selectedOfferData}
        />
      )}
      <div className="iLendingCDX-section-divider"></div>
      <div>
        {checklistItems.map((item, index) => (
          <div key={index}>
            <div className={`iLendingCDX-checklist-item-container ${item.disabled && 'iLendingCDX-checklist-item-disabled'}`}>
              <div className="iLendingCDX-checklist-item">
                <h1 className="iLendingCDX-checklist-title">{item.title}</h1>
              </div>
              <div className="iLendingCDX-checklist-image-container">
                <img
                  src={item.completed ? `${AssetLinks.greenTick}` : `${AssetLinks.checkGreyWhite}`}
                  alt={item.completed ? 'Completed' : 'Pending'}
                />
              </div>
            </div>
            <div className="iLendingCDX-section-divider"></div>
          </div>
        ))}
      </div>
      <div>
        <h2 className="iLendingCDX-title__thank-you--small">{t('thankYou.nextSteps')}</h2>
        <div className="iLendingCDX-nextsteps_row">
          <img
            src={AssetLinks.phoneIcon}
            alt=""
          />
          <p>{t('thankYou.callFromIlending')}</p>
        </div>
        <div className="iLendingCDX-consultant-phone-call-container"></div>
        <div className="iLendingCDX-nextsteps_row">
          <img
            src={AssetLinks.chatIcon}
            alt=""
          />
          <p>{t('thankYou.briefConversation')}</p>
        </div>
        <div className="iLendingCDX-nextsteps_row">
          <img
            src={AssetLinks.paymentIcon}
            alt=""
          />
          <p>{t('thankYou.skippingPayment')}</p>
        </div>
        <span className="iLendingCDX-footer-divider">
          <span
            className="iLendingCDX-footer-icon iLendingCDX-mt-4 iLendingCDX-mb-4"
            alt="shield-icon"
          >
            <b> {t('thankYou.or')}</b>
          </span>
        </span>
        <div className="iLendingCDX-nextsteps_row">
          <img
            src={AssetLinks.phoneCall}
            alt=""
          />
          <div className="iLendingCDX-">
            <p className="iLendingCDX-callus--text ">
              <a
                href={'tel:' + t('contactPhoneNumber').replace(/-/g, '')}
                onClick={handlePhoneNumberClick}
              >
                {t('thankYou.callUs')} {t('contactPhoneNumber')}
              </a>
            </p>

            <p>{t('thankYou.forAssistance')}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OffRampPage;
